/**
 * useNavCounts — what is waiting, where, and only if you can act on it.
 *
 * THE COUNTING RULE (CLAUDE.md, locked 2026-08-13): the number counts ONLY
 * items the user can act on. Never a potential, never a total row count. An item
 * with no route, no recipient, or already archived is not counted. Every counter
 * lives in public.nav_counters and its count_sql is written to that rule; this
 * hook does no counting of its own.
 *
 * THE PILL FOLLOWS ATTENTION THROUGH THE COLLAPSE:
 *   suite collapsed  → one pill on the heading, the total across its pages
 *   suite expanded   → the pill moves onto the pages that actually hold items
 *   page open        → the number STAYS while you are on it, it does not clear
 *                      on arrival
 *   you navigate away→ it clears, because now you have seen it
 *
 * Seen-state is server-side, per user and per page (public.nav_counter_seen), so
 * it survives a refresh and a different device. It is written on LEAVING a page,
 * never on arriving at one.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { countProspectorVisible } from "@/lib/prospector-visible";

export interface NavCount {
  nav_key: string;
  suite_key: string;
  label: string;
  tooltip: string;
  n: number;
  seen_count: number;
  /** true when there is something here the user has not already looked at */
  unseen: boolean;
}

/**
 * Which nav_key a route belongs to.
 *
 * Matched longest-first on pathname + search, so
 * /dashboard/pipeline-crm?tab=inbox resolves to the inbox rather than to the
 * command centre that shares its path.
 */
const ROUTE_TO_KEY: { match: string; nav_key: string }[] = [
  { match: "/dashboard/pipeline-crm?tab=inbox", nav_key: "crm:inbox" },
  { match: "/dashboard/pipeline-crm?tab=pipeline", nav_key: "crm:deal-companies" },
  { match: "/dashboard/pipeline-crm", nav_key: "crm:command-center" },
  { match: "/dashboard/press-requests", nav_key: "journalism:press-requests" },
  { match: "/dashboard/engage", nav_key: "engage:hq" },
  { match: "/dashboard/dealflow", nav_key: "signals:prospector" },
  { match: "/dashboard/hr-tools/talent-crm", nav_key: "recruit:talent-crm" },
  { match: "/dashboard/evaluate", nav_key: "recruit:evaluate" },
];

export function navKeyForRoute(pathname: string, search: string): string | null {
  const full = `${pathname}${search || ""}`;
  let best: { nav_key: string; len: number } | null = null;
  for (const r of ROUTE_TO_KEY) {
    if (full.startsWith(r.match) && (!best || r.match.length > best.len)) {
      best = { nav_key: r.nav_key, len: r.match.length };
    }
  }
  return best?.nav_key ?? null;
}

export function useNavCounts() {
  const [counts, setCounts] = useState<Record<string, NavCount>>({});
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();

  // What page we were on last, and what its number was when we were there. Both
  // are needed to write the seen-state on the way OUT.
  const onPage = useRef<{ nav_key: string; n: number } | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc("nav_counts" as any);
    if (error) {
      // A nav that cannot count shows no pills. It never guesses.
      console.warn("[nav-counts] could not load:", error.message);
      setLoaded(true);
      return;
    }
    const map: Record<string, NavCount> = {};
    for (const row of ((data as NavCount[]) || [])) map[row.nav_key] = row;

    // THE PROSPECTOR PILL IS THE PROSPECTOR'S OWN QUERY.
    //
    // Every other counter is a count_sql row on the server. This one is not,
    // deliberately: the Prospector list and this number must be the same set,
    // and the only way to guarantee that is for both to call the same helper —
    // src/lib/prospector-visible.ts, which Dealflow.tsx calls to build its list.
    // A second description of "visible", however carefully copied, is how 171
    // rows judged "no" ended up counted on a pill and rendered on a page that
    // had each decided the question separately.
    try {
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess?.session?.user?.id;
      if (uid && map["signals:prospector"]) {
        let reachableOnly = true;
        try { reachableOnly = localStorage.getItem("prospector_reachable_only") !== "0"; } catch { /* noop */ }
        const n = await countProspectorVisible(supabase, uid, { reachableOnly });
        // null = the count could not be made. Leave the server's number rather
        // than showing a zero that means "we did not ask".
        if (n !== null) map["signals:prospector"] = { ...map["signals:prospector"], n };
      }
    } catch (e) {
      console.warn("[nav-counts] prospector count skipped:", (e as any)?.message);
    }

    setCounts(map);
    setLoaded(true);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Refresh periodically so a pill appears when something arrives while the tab
  // is open. Quiet enough not to be a poller anybody notices.
  useEffect(() => {
    const t = setInterval(() => { void load(); }, 120_000);
    // T1293: a send (or an answer recorded) drops its badge at once, not two minutes later.
    const now = () => { void load(); };
    window.addEventListener("mf:nav-counts-refresh", now);
    return () => { clearInterval(t); window.removeEventListener("mf:nav-counts-refresh", now); };
  }, [load]);

  /**
   * THE CLEAR HAPPENS ON THE WAY OUT, NOT THE WAY IN.
   *
   * Arriving at a page does not clear its pill — the user should be able to see
   * "21" while they work through the 21. Leaving is the moment they have seen
   * it, so that is when the seen-state is written and the pill goes.
   */
  useEffect(() => {
    const key = navKeyForRoute(location.pathname, location.search);
    const previous = onPage.current;

    if (previous && previous.nav_key !== key) {
      const leaving = previous;
      void (async () => {
        const { error } = await supabase.rpc("nav_mark_seen" as any, {
          p_nav_key: leaving.nav_key, p_count: leaving.n,
        });
        if (error) console.warn("[nav-counts] seen-state not saved:", error.message);
        setCounts((prev) => {
          const cur = prev[leaving.nav_key];
          if (!cur) return prev;
          return { ...prev, [leaving.nav_key]: { ...cur, seen_count: leaving.n, unseen: false } };
        });
      })();
    }

    onPage.current = key ? { nav_key: key, n: counts[key]?.n ?? 0 } : null;
  }, [location.pathname, location.search, counts]);

  /** The number to show on a page, or 0 for no pill. */
  const forPage = useCallback((navKey?: string | null): number => {
    if (!navKey) return 0;
    const c = counts[navKey];
    if (!c) return 0;
    // While the user is ON the page, the number stays regardless of seen-state.
    const here = navKeyForRoute(location.pathname, location.search) === navKey;
    if (here) return c.n;
    return c.unseen ? c.n : 0;
  }, [counts, location.pathname, location.search]);

  /** The total to show on a collapsed suite heading, or 0 for no pill. */
  const forSuite = useCallback((suiteKey?: string | null): number => {
    if (!suiteKey) return 0;
    let total = 0;
    for (const c of Object.values(counts)) {
      if (c.suite_key !== suiteKey) continue;
      total += forPage(c.nav_key);
    }
    return total;
  }, [counts, forPage]);

  const tooltipFor = useCallback((navKey?: string | null): string | undefined => {
    if (!navKey) return undefined;
    return counts[navKey]?.tooltip;
  }, [counts]);

  /** The suite heading's tooltip: what the total is made of, page by page. */
  const suiteTooltip = useCallback((suiteKey: string, suiteLabel: string): string => {
    const parts = Object.values(counts)
      .filter((c) => c.suite_key === suiteKey && forPage(c.nav_key) > 0)
      .map((c) => `${forPage(c.nav_key)} in ${c.label}`);
    if (!parts.length) return `Nothing waiting in ${suiteLabel}.`;
    return `${parts.join(", ")}. Open ${suiteLabel} to see which page. Only things you can act on are counted.`;
  }, [counts, forPage]);

  return { counts, loaded, forPage, forSuite, tooltipFor, suiteTooltip, reload: load };
}
